import React, { useState, useRef } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useRouter, NextRouter } from 'next/router';
import Link from 'next/link';
import DefaultErrorPage from 'next/error';
import { format } from 'date-fns';
import CloseModalIcon from 'components/Icons/CloseModalIcon';
import Arrow from 'components/Icons/Arrow';
import List from 'components/churchPage/list';
import StateHandler from 'components/stateHandler/stateHandler';
import config from '../../config';
import {
  useSingleEvent,
  useCategories,
  useEvents,
  getUniqueChildCategories,
  useSingleCategoryFromAccount,
  useSocket,
  useSessionStorage,
} from 'hooks';
import { BASE_CATEGORY_SLUG, passwordSignInToEvent } from 'lib/api';
import Spinner from 'components/spinner';

const Chat = dynamic(() => import('./chat'), { ssr: false });

const CloseBtn = ({ prevPath, router }) => (
  <div className="w-full flex justify-end pt-4 pr-4 phone-200:pt-2 phone-200:pr-2 absolute">
    <button
      role="button"
      className="bg-blue text-black px-3 py-1.5 text-base rounded-full flex items-center z-[2] relative phone-200:p-2"
      onClick={() => goBack(prevPath, router)}
    >
      <CloseModalIcon className="w-[12px] h-[12px] phone-200:w-[8px] phone-200:h-[8px]" />
      <span className="pl-2.5 phone-200:hidden"> Sulje</span>
    </button>
  </div>
);

const goBack = (prevPath: string, router: NextRouter) => {
  if (prevPath) {
    return router.back();
  }
  return router.push('/');
};

const EventContent = ({ slug, eventUrl, renderError }) => {
  if (renderError) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <DefaultErrorPage statusCode={renderError} />
      </>
    );
  }
  const passwordRef = useRef(null);
  const socket = useSocket();
  const queryClient = useQueryClient();
  const [showMoreText, setShowMoreText] = useState(false);
  const router = useRouter();
  const prevPath = useSessionStorage('prevPath');
  const { data: eventData, error: eventError, isLoading } = useSingleEvent(slug, eventUrl);

  const { data: church } = useSingleCategoryFromAccount(slug);
  const { data: churches } = useCategories(
    BASE_CATEGORY_SLUG,
    {
      accountUrls: slug,
      include: 'children',
    },
    {
      enabled: !!slug,
    }
  );

  const [password, setPassword] = useState('');
  const [eventId, setEventId] = useState(null);
  const [signingIn, setSigninIn] = useState(null);
  const [signInError, setSignInError] = useState(false);

  const passwordChangeHandler = (e) => {
    if (signInError) setSignInError(null);
    setPassword(e.target.value);
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    setSigninIn(true);
    try {
      await passwordSignInToEvent(slug, eventUrl, eventId, password);
      queryClient.invalidateQueries('single-event');
    } catch (error) {
      setSignInError(true);
      setPassword('');
      passwordRef.current.focus();
    }
    setSigninIn(null);
  };

  const updateEventData = (data) => {
    const Q = { accountSlug: slug, eventSlug: eventUrl };
    queryClient.setQueryData(['single-event', Q], {
      ...eventData,
      ...data,
    });
  };

  const allCategorySlugs: any | string[] = churches && getUniqueChildCategories(churches);
  const slugs = allCategorySlugs && allCategorySlugs.map((cs) => cs.slug);
  const { data: preliveEvents } = useEvents(
    slugs,
    {
      accountUrls: slug as string,
      states: [3],
      startDate: format(new Date(), 'yyyy-MM-dd'),
    },
    {
      enabled: !!slug && !!slugs,
    }
  );
  const relevantPreliveEvents = preliveEvents?.events?.filter((e) => e.urlName !== eventUrl);
  const { data: onDemandEvents } = useEvents(
    slugs,
    {
      accountUrls: slug as string,
      states: [1],
      endDate: format(new Date(), 'yyyy-MM-dd'),
    },
    {
      enabled: !!slug && !!slugs,
    }
  );
  const relevantOnDemandEvents = onDemandEvents?.events?.filter((e) => e.urlName !== eventUrl);

  const { data: eventWithCategories } = useEvents(
    slugs,
    {
      accountUrls: slug as string,
      search: eventData?._id,
      limit: 1,
    },
    {
      enabled: !!eventData?._id && !!slugs,
    }
  );

  const socketUpdates = () => {
    socket.on('eventUpdated', (data) => {
      // just invalidate the single-event query so it will fetch the event again for the client
      // queryClient.invalidateQueries(["single-event"]);
      if (eventData.state !== data.state) {
        queryClient.invalidateQueries(['single-event']);
      } else {
        updateEventData({ ...data, account: eventData.account });
      }
    });

    socket.on('eventStateUpdated', (data) => {
      if (data.state !== eventData.state) {
        queryClient.invalidateQueries(['single-event']);
      }
    });
  };

  const playerWithSlides =
    eventData?.slides?.enabled &&
    eventData?.slides?.set?.length > 0 &&
    ((eventData?.state === 1 && eventData?.sync?.slides?.length > 0) || eventData?.state === 0);

  useEffect(() => {
    router.prefetch('/');
  }, []);

  useEffect(() => {
    const initws = () => {
      const { _id: eventId } = eventData;

      const emitReceiveUpdates = () => {
        socket.emit('receiveEventUpdates', eventId);
        socket.emit('receiveEventStateUpdates', eventId);
        socket.emit('playerStart', {
          eventId,
        });
      };

      emitReceiveUpdates();
      socket.io.on('reconnect', emitReceiveUpdates);

      socketUpdates();
    };
    if (!!socket && !!eventData) {
      initws();
    }

    return () => {
      if (socket) {
        socket.off('eventUpdated');
        socket.off('eventStateUpdated');
        if (eventData?._id) {
          socket.emit('leavePlayerRoom', {
            eventId: eventData._id,
          });
        }
      }
    };
  }, [socket, eventData]);

  if (!eventError && isLoading) {
    return (
      <section className={`w-full h-236 grid items-center justify-center`}>
        <Spinner />
      </section>
    );
  }

  if (eventError) {
    if (eventError instanceof Error) {
      if (eventError.message.includes('401')) {
        // Return the password modal
        const Q = {
          accountSlug: slug,
          eventSlug: eventUrl,
        };
        const state = queryClient.getQueryState<any, any>(['single-event', Q]);
        const errorData = state?.error?.response?.data;
        if (errorData.error.errors.find((e) => e.protection === 'password')) {
          const { eventTitle, eventId: eid } = errorData;
          if (!eventId) {
            setEventId(eid);
          }
          return (
            <>
              <Head>
                <title key="title">{`${eventTitle} - Virtuaalikirkko`}</title>
                <meta property="og:url" key="og:url" content={`${config?.domain}/kirkko/${slug}/${eventUrl}`} />
                <meta name="title" key="m-title" content={`${eventTitle} ─ Virtuaalikirkko`} />
                <meta property="og:title" key="og:title" content={`${eventTitle} ─ Virtuaalikirkko`} />
                <meta name="description" key="description" content={eventData?.about?.description} />
                <meta property="og:description" key="og:description" content={eventData?.about?.description} />
                <meta
                  property="og:image"
                  key="og:image"
                  content={`${config.cdn}${eventData?.previewImg}` || '/images/bg1.jpeg'}
                />
              </Head>
              <section className={`w-full h-full`}>
                <CloseBtn prevPath={prevPath} router={router} />
                <div className="p-8">
                  <h1 className="text-2xl font-bold mb-4 phone-100:text-lg">{eventTitle}</h1>
                  <p className="mb-4">Tämä tapahtuma vaatii toimiakseen salasanan.</p>
                  <form className="max-w-sm" onSubmit={handleSubmitPassword}>
                    <label className="block text-grey-darker text-sm font-bold mb-2" htmlFor="password">
                      Syötä salasana
                    </label>
                    <input
                      onChange={passwordChangeHandler}
                      value={password}
                      className="w-full py-2 px-3 mb-4 shadow appearance-none border focus:ring-2 focus:ring-blue border-gray rounded "
                      id="password"
                      type="password"
                      placeholder="************"
                      ref={passwordRef}
                    />

                    <div
                      className={`bg-red-100 border-l-4 border-red-500 text-red-700 transition-all overflow-hidden duration-300 transform  ${
                        signInError ? 'h-full opacity-100 p-4 mb-4 translate-x-0' : 'opacity-0 h-[0px] -translate-x-16'
                      }`}
                      role="alert"
                    >
                      <p className="font-bold">Väärä salasana</p>
                      <p>Syötit väärän salasanan. Yritä uudestaan.</p>
                    </div>

                    <button
                      disabled={password.length === 0 || signingIn}
                      className="bg-blue hover:bg-[#0e6db5] text-white font-bold py-2 px-4 rounded disabled:bg-gray-300 disabled:cursor-default"
                      type="submit"
                    >
                      {signingIn ? <>Kirjaudutaan...</> : <>Kirjaudu tapahtumaan</>}
                    </button>
                  </form>
                </div>
              </section>
            </>
          );
        }
      }
      if (eventError.message.includes('404')) {
        return (
          <>
            <Head>
              <meta name="robots" content="noindex" />
            </Head>
            <DefaultErrorPage statusCode={404} />
          </>
        );
      }
    }
  }

  return (
    <>
      <Head>
        <title key="title">{eventData?.title} ─ Virtuaalikirkko</title>
        <meta name="title" key="m-title" content={`${eventData?.title} ─ Virtuaalikirkko`} />
        <meta property="og:url" key="og:url" content={`${config?.domain}/kirkko/${slug}/${eventUrl}`} />
        <meta property="og:title" key="og:title" content={`${eventData?.title} ─ Virtuaalikirkko`} />
        <meta name="description" key="description" content={eventData?.about?.description} />
        <meta property="og:description" key="og:description" content={eventData?.about?.description} />
        <meta
          property="og:image"
          key="og:image"
          content={`${config.cdn}${eventData?.previewImg}` || '/images/bg1.jpeg'}
        />
      </Head>
      <section
        style={{
          backgroundImage: eventData?.previewImg
            ? `url(${config.cdn}${eventData.previewImg})`
            : 'url(/images/bg1.jpeg)',
          height: !playerWithSlides ? 'calc(90vw * 0.5625)' : 'auto',
          maxHeight: !playerWithSlides ? '48.1640625rem' : 'auto',
        }}
        className={`w-full h-full bg-top bg-no-repeat bg-cover relative ${
          eventData?.state === 3 ? 'phone-300:mb-[80px]' : ''
        }`}
      >
        <CloseBtn prevPath={prevPath} router={router} />
        <div className={!playerWithSlides ? 'absolute top-0 right-0 left-0 bottom-0' : 'min-h-[300px] bg-gray-100'}>
          {eventData && eventWithCategories && (
            <StateHandler
              data={{
                ...eventData,
                categories: eventWithCategories?.events?.[0]?.categories,
              }}
              church={church}
            />
          )}
        </div>
      </section>

      <div className="flex justify-center items-center pl-[85px] pr-[70px] tablet:px-6">
        <div className="flex flex-col w-full max-w-churchPage">
          <div className="flex justify-start flex-wrap">
            <div
              className={`pt-16 pr-8 flex-1 min-w-[250px] ${
                eventData?.state === 3 ? 'tablet-100:pt-[7.5rem]' : 'phone-200:pt-6'
              } ${playerWithSlides ? 'pt-6' : ''}`}
            >
              {[0, 1].includes(eventData?.state) && eventData?.chatboard?.show && (
                <div className="mb-8">
                  <Chat event={eventData} />
                </div>
              )}
              <h2 className="text-2xl pb-8 font-bold phone-100:text-lg">{eventData?.title}</h2>{' '}
              <div className="text-bodyText max-w-xl">
                {eventData?.about?.show && (
                  <div
                    className={`prose laptop:text-lg phone-100:text-base ${
                      !showMoreText && eventData?.about?.descriptionHTML?.length >= 400
                        ? 'h-[450px] overflow-hidden'
                        : ''
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: eventData?.about?.descriptionHTML || '',
                    }}
                  />
                )}
                {eventData?.about?.show && !showMoreText && eventData?.about?.descriptionHTML?.length >= 400 && (
                  <div className="absolute mt-[-50px] h-[50px] w-full bg-gradient-to-b from-whiteTransparent to-white"></div>
                )}
                {eventData?.about?.show && eventData?.about?.descriptionHTML?.length >= 400 && (
                  <button
                    role="button"
                    className="pt-4 flex items-center"
                    type="button"
                    onClick={() => {
                      setShowMoreText(!showMoreText);
                    }}
                  >
                    <span className="mr-4">Näytä {showMoreText ? 'vähemmän' : 'lisää'}</span>
                    <span
                      className={`"transition duration-300 ease-in-out "${
                        showMoreText ? 'transform -rotate-90' : 'transform rotate-90'
                      } `}
                    >
                      <Arrow />
                    </span>
                  </button>
                )}
                {eventData?.about?.show && eventData?.about?.image && (
                  <img
                    className="object-contain mt-10 w-full h-auto rounded-[10px]"
                    src={`${config.cdn + eventData.about.image}`}
                  />
                )}
                {eventData?.attachments?.enabled &&
                  (![2, 3].includes(eventData?.state) ||
                    ([2, 3].includes(eventData?.state) && eventData?.layout?.attachments?.showInHidden)) && (
                    <div>
                      <h3 className="text-2xl font-bold pt-16 phone-100:text-lg">TIEDOSTOT</h3>
                      <ul className="py-2 flex flex-wrap">
                        {eventData?.attachments?.files.map((item) => (
                          <li
                            key={item.path}
                            className="border-1 border-menuBorder rounded-10 pointer p-[10px] m-[5px] transition-all duration-200 ease-linear delay hover:shadow-item cursor-pointer min-w-[100px] first:ml-0 tablet-100:text-lg"
                          >
                            <a href={config.cdn + item.path} target="_blank">
                              {item.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>
            <div
              className={`pt-16 ${eventData?.state === 3 ? 'tablet-100:pt-[7.5rem] phone:pt-12' : 'phone-200:pt-6'} ${
                playerWithSlides ? 'pt-6' : ''
              }`}
            >
              {eventData?.chapters?.enabled && eventData?.chapters?.set?.length > 0 && (
                <div className="w-[200px]">
                  <h4 className="pb-10 font-bold text-2xl phone-100:text-lg">
                    {eventData?.layout?.chapters?.title || 'Virret ja musiikki'}
                  </h4>
                  <div>
                    <ul className="text-bodyText laptop:text-lg phone-100:text-base">
                      {eventData &&
                        eventData?.chapters?.set.map((chapter) => {
                          return (
                            <li key={chapter?.name}>
                              <div>{chapter?.name}</div>
                              <div>{chapter?.text}</div>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                </div>
              )}
              {/*<Link href={`/lahjoita/${church?.account?.slug}`}>
                <a className="rounded-full bg-blue text-white py-4 mx-auto w-48 mt-6 block text-center text-xl flex items-center justify-center">
                  <PaymentCardIcon />
                  <span className="ml-3">Lahjoita</span>
                </a>
                      </Link>*/}
            </div>
          </div>
          {relevantPreliveEvents?.length > 0 && (
            <section className="flex justify-center items-center">
              <List
                itemNb={5}
                events={relevantPreliveEvents}
                link={'/tulevat-lahetykset'}
                header={'MUITA TULEVIA LÄHETYKSIÄ TÄÄLTÄ'}
              />
            </section>
          )}
          {relevantOnDemandEvents?.length > 0 && (
            <section className="flex justify-center items-center">
              <List
                itemNb={10}
                events={relevantOnDemandEvents}
                link={'/menneet-lahetykset'}
                header={'MENNEET LÄHETYKSET'}
              />
            </section>
          )}
          {church && (
            <section className="mb-24 pt-32 phone-200:pt-10 phone-200:mb-12">
              <h2 className="text-2xl font-bold pb-7 phone-100:text-lg">TIETOA KIRKOSTA</h2>
              <div>
                <div className="text-bodyText max-w-xl">
                  <div
                    className="prose prose-xl laptop:text-lg phone-100:text-base"
                    dangerouslySetInnerHTML={{
                      __html: church?.descriptionParsed?.contentHTML,
                    }}
                  ></div>
                </div>
              </div>
              <Link href={`/?churchSlug=${slug}`} as={`/kirkko/${slug}`}>
                <a
                  role="button"
                  type="button"
                  className="appearance-none inline-block bg-blue rounded-full px-10 py-4 mt-8 text-bodyText text-white phone-100:px-6 phone-100:py-2 phone-100:text-lg"
                >
                  Lue lisää
                </a>
              </Link>
            </section>
          )}
        </div>
      </div>
    </>
  );
};

export default EventContent;
